import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Upload, Loader2, X, ImagePlus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** Turn "/api/uploads/…" into a fully-qualified URL against REACT_APP_BACKEND_URL. */
export function resolveImageUrl(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/api/")) return `${BACKEND}${value}`;
  return value;
}

/**
 * ImageUploader — file picker with preview.
 * Accepts an optional existing URL (either uploaded or external).
 */
export default function ImageUploader({ value, onChange, testidPrefix = "img" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const preview = resolveImageUrl(value);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow same file re-select
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please pick an image file (JPG, PNG, WebP, or GIF).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr("Image must be under 8 MB.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Upload failed. Try again?");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        data-testid={`${testidPrefix}-file-input`}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={onFile}
        className="hidden"
      />
      {preview ? (
        <div
          data-testid={`${testidPrefix}-preview`}
          className="relative rounded-2xl overflow-hidden border border-ink-soft bg-app group"
        >
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              data-testid={`${testidPrefix}-replace-btn`}
              onClick={pick}
              disabled={busy}
              className="bg-white/90 hover:bg-white text-ink font-semibold text-xs px-3 py-1.5 rounded-full shadow"
            >
              {busy ? "…" : "Replace"}
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-clear-btn`}
              onClick={clear}
              className="bg-red-600/90 hover:bg-red-700 text-white text-xs h-7 w-7 rounded-full grid place-items-center shadow"
              title="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testidPrefix}-upload-btn`}
          onClick={pick}
          disabled={busy}
          className="w-full rounded-2xl border-2 border-dashed border-ink-soft bg-app px-6 py-10 flex flex-col items-center justify-center gap-2 hover:border-[var(--cm-primary)] hover:bg-[var(--cm-primary)]/5 transition-colors disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-[var(--cm-primary)]" />
              <span className="text-sm text-ink-soft">Uploading…</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-6 w-6 text-[var(--cm-primary)]" />
              <span className="text-sm font-semibold">Add a photo</span>
              <span className="text-xs text-ink-soft">JPG, PNG, WebP or GIF · up to 8 MB</span>
            </>
          )}
        </button>
      )}
      {err && (
        <div data-testid={`${testidPrefix}-upload-error`} className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
          {err}
        </div>
      )}
      <details className="text-xs text-ink-soft">
        <summary className="cursor-pointer select-none hover:text-ink">Or paste an image URL</summary>
        <input
          data-testid={`${testidPrefix}-url-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-2 bg-surface border border-ink-soft rounded-xl px-3 py-2 text-sm"
          placeholder="https://…"
        />
      </details>
    </div>
  );
}
